<template>
  <div class="express-admin-page">
    <div class="page-shell">
      <div class="page-header">
        <div>
          <h1>售后快递管理</h1>
          <p>查看手机端上传的快递二维码识别记录，并完成核对状态管理</p>
        </div>
        <div class="header-actions">
          <button type="button" class="light-btn" @click="openScanPage">打开手机端上传页</button>
          <button type="button" class="primary-btn" @click="fetchRecords">刷新列表</button>
        </div>
      </div>

      <div class="filter-card">
        <div class="filter-grid">
          <div class="filter-item">
            <label>运单号</label>
            <input
              v-model.trim="filters.trackingNumber"
              type="text"
              placeholder="请输入运单号"
              @keyup.enter="handleSearch"
            />
          </div>
          <div class="filter-item">
            <label>核对状态</label>
            <select v-model="filters.checkedFlag">
              <option value="">全部</option>
              <option value="0">未核对</option>
              <option value="1">已核对</option>
            </select>
          </div>
          <div class="filter-buttons">
            <button type="button" class="primary-btn" @click="handleSearch">搜索</button>
            <button type="button" class="light-btn" @click="resetFilters">重置</button>
          </div>
        </div>
      </div>

      <div v-if="loading" class="empty-block">正在加载记录...</div>
      <div v-else-if="!records.length" class="empty-block">暂无快递记录</div>

      <div v-else class="record-list">
        <div v-for="item in records" :key="item.id" class="record-card">
          <div class="record-image" @click="openImagePreview(item.imageUrl)">
            <img :src="item.imageUrl" :alt="item.trackingNumber || '快递图片'" />
          </div>
          <div class="record-content">
          <div class="record-top">
            <div class="tracking-number-section">
              <div class="record-label">运单号</div>
              <div v-if="editingId !== item.id" class="tracking-number-display">
                <span class="record-value tracking">{{ item.trackingNumber || '-' }}</span>
                <button type="button" class="edit-tracking-btn" @click="startEdit(item)">编辑</button>
              </div>
              <div v-else class="tracking-number-edit">
                <input
                  v-model="editingTrackingNumber"
                  class="edit-tracking-input"
                  type="text"
                  placeholder="请输入运单号"
                />
                <div class="tracking-edit-actions">
                  <button type="button" class="save-tracking-btn" @click="saveTrackingNumber(item)">保存</button>
                  <button type="button" class="cancel-tracking-btn" @click="cancelEdit">取消</button>
                </div>
              </div>
            </div>
            <div class="status-and-delete">
              <span class="status-tag" :class="item.checkedFlag === 1 ? 'checked' : 'unchecked'">
                {{ item.checkedFlag === 1 ? '已核对' : '未核对' }}
              </span>
              <button
                type="button"
                class="delete-btn"
                :disabled="updatingId === item.id"
                @click="deleteRecord(item)"
              >
                删除
              </button>
            </div>
          </div>

          <div class="record-grid">
            <div class="info-block">
              <span class="record-label">上传时间</span>
              <span class="record-value">{{ formatDateTime(item.createTime) }}</span>
            </div>
            <div class="info-block">
              <span class="record-label">核对时间</span>
              <span class="record-value">{{ formatDateTime(item.checkedTime) }}</span>
            </div>
          </div>

            <div class="record-actions">
              <button
                type="button"
                class="primary-btn primary-btn-large"
                :disabled="updatingId === item.id"
                @click="toggleChecked(item)"
              >
                {{ updatingId === item.id ? '处理中...' : (item.checkedFlag === 1 ? '改为未核对' : '标记已核对') }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div class="bottom-bar">
        <div class="summary-mini">
          <span>共 <strong>{{ total }}</strong> 条</span>
          <span>第 <strong>{{ pageNum }}</strong> 页</span>
        </div>
        <div class="pagination-bar">
          <button type="button" class="light-btn" :disabled="pageNum <= 1 || loading" @click="changePage(pageNum - 1)">
            上一页
          </button>
          <span class="page-text">第 {{ pageNum }} / {{ totalPages }} 页</span>
          <button
            type="button"
            class="light-btn"
            :disabled="pageNum >= totalPages || loading"
            @click="changePage(pageNum + 1)"
          >
            下一页
          </button>
        </div>
      </div>

      <!-- 图片预览弹窗 -->
      <div v-if="previewImageUrl" class="image-preview-overlay" @click="closeImagePreview">
        <div class="image-preview-container" @click.stop>
          <div class="preview-header">
            <span class="preview-title">图片预览</span>
            <button type="button" class="preview-close-btn" @click="closeImagePreview">×</button>
          </div>
          <div class="preview-image-wrapper">
            <img :src="previewImageUrl" alt="预览图片" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import API_CONFIG from '@/api/config'

export default {
  name: 'ExpressAdmin',
  data() {
    return {
      loading: false,
      updatingId: null,
      records: [],
      total: 0,
      pageNum: 1,
      pageSize: 10,
      filters: {
        trackingNumber: '',
        checkedFlag: '0'
      },
      previewImageUrl: null,
      editingId: null,
      editingTrackingNumber: ''
    }
  },
  computed: {
    totalPages() {
      const pages = Math.ceil((Number(this.total) || 0) / this.pageSize)
      return pages > 0 ? pages : 1
    }
  },
  created() {
    this.applyQueryFromRoute()
    this.fetchRecords()
  },
  watch: {
    '$route.query': {
      deep: true,
      handler() {
        this.applyQueryFromRoute()
      }
    }
  },
  methods: {
    applyQueryFromRoute() {
      const query = this.$route.query || {}
      this.pageNum = 1
      this.pageSize = 10
      this.filters = {
        trackingNumber: '',
        checkedFlag: '0'
      }
      if (query.pageNum) {
        this.pageNum = Math.max(1, Number(query.pageNum) || 1)
      }
      if (query.pageSize) {
        this.pageSize = Math.max(1, Number(query.pageSize) || 10)
      }
      if (typeof query.trackingNumber === 'string') {
        this.filters.trackingNumber = query.trackingNumber
      }
      if (query.checkedFlag === '0' || query.checkedFlag === '1' || query.checkedFlag === '') {
        this.filters.checkedFlag = query.checkedFlag
      }
    },
    syncRouteQuery() {
      const query = {
        pageNum: String(this.pageNum),
        pageSize: String(this.pageSize)
      }
      if (this.filters.trackingNumber) {
        query.trackingNumber = this.filters.trackingNumber
      }
      if (this.filters.checkedFlag !== '') {
        query.checkedFlag = String(this.filters.checkedFlag)
      }
      this.$router.replace({ path: this.$route.path, query }).catch(() => {})
    },
    async fetchRecords() {
      this.loading = true
      this.syncRouteQuery()
      try {
        const response = await axios.get(API_CONFIG.expressList(), {
          params: {
            pageNum: this.pageNum,
            pageSize: this.pageSize,
            trackingNumber: this.filters.trackingNumber || undefined,
            checkedFlag: this.filters.checkedFlag === '' ? undefined : this.filters.checkedFlag
          }
        })
        const payload = response.data || {}
        this.records = Array.isArray(payload.rows) ? payload.rows : []
        this.total = Number(payload.total) || 0
      } catch (error) {
        this.records = []
        this.total = 0
        alert(error.response?.data?.msg || '获取快递记录失败')
      } finally {
        this.loading = false
      }
    },
    handleSearch() {
      this.pageNum = 1
      this.fetchRecords()
    },
    resetFilters() {
      this.filters = {
        trackingNumber: '',
        checkedFlag: '0'
      }
      this.pageNum = 1
      this.fetchRecords()
    },
    changePage(nextPage) {
      if (nextPage < 1 || nextPage > this.totalPages || nextPage === this.pageNum) {
        return
      }
      this.pageNum = nextPage
      this.fetchRecords()
    },
    async toggleChecked(item) {
      if (!item || !item.id) {
        return
      }
      this.updatingId = item.id
      try {
        const checkedFlag = item.checkedFlag === 1 ? 0 : 1
        const response = await axios.post(API_CONFIG.expressCheck(), {
          id: item.id,
          checkedFlag
        })
        if (Number(response.data?.code) !== 200) {
          throw new Error(response.data?.msg || '更新状态失败')
        }
        item.checkedFlag = checkedFlag
        item.checkedBy = checkedFlag === 1 ? (response.data?.data?.checkedBy || item.checkedBy) : null
        item.checkedTime = checkedFlag === 1 ? new Date().toISOString() : null
      } catch (error) {
        alert(error.response?.data?.msg || error.message || '更新状态失败')
      } finally {
        this.updatingId = null
      }
    },
    formatDateTime(value) {
      if (!value) {
        return '-'
      }
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) {
        return String(value)
      }
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      const hour = String(date.getHours()).padStart(2, '0')
      const minute = String(date.getMinutes()).padStart(2, '0')
      const second = String(date.getSeconds()).padStart(2, '0')
      return `${year}-${month}-${day} ${hour}:${minute}:${second}`
    },
    openScanPage() {
      const routeData = this.$router.resolve({
        path: '/express-scan'
      })
      window.open(routeData.href, '_blank')
    },
    openImagePreview(imageUrl) {
      this.previewImageUrl = imageUrl
      document.body.style.overflow = 'hidden'
    },
    closeImagePreview() {
      this.previewImageUrl = null
      document.body.style.overflow = ''
    },
    startEdit(item) {
      this.editingId = item.id
      this.editingTrackingNumber = item.trackingNumber
    },
    cancelEdit() {
      this.editingId = null
      this.editingTrackingNumber = ''
    },
    async saveTrackingNumber(item) {
      if (!this.editingTrackingNumber.trim()) {
        alert('运单号不能为空')
        return
      }
      this.updatingId = item.id
      try {
        const response = await axios.post(API_CONFIG.getFullUrl(API_CONFIG.ENDPOINTS.EXPRESS_UPDATE_TRACKING), {
          id: item.id,
          trackingNumber: this.editingTrackingNumber.trim()
        })
        if (Number(response.data?.code) === 200) {
          item.trackingNumber = this.editingTrackingNumber.trim()
          this.cancelEdit()
          alert('修改成功')
        } else {
          alert(response.data?.msg || '修改失败')
        }
      } catch (error) {
        alert(error.response?.data?.msg || '修改失败')
      } finally {
        this.updatingId = null
      }
    },
    async deleteRecord(item) {
      if (!confirm('确定要删除这条记录吗？')) {
        return
      }
      this.updatingId = item.id
      try {
        const response = await axios.post(API_CONFIG.getFullUrl(API_CONFIG.ENDPOINTS.EXPRESS_DELETE), {
          id: item.id
        })
        if (Number(response.data?.code) === 200) {
          this.records = this.records.filter(r => r.id !== item.id)
          this.total -= 1
          alert('删除成功')
        } else {
          alert(response.data?.msg || '删除失败')
        }
      } catch (error) {
        alert(error.response?.data?.msg || '删除失败')
      } finally {
        this.updatingId = null
      }
    }
  }
}
</script>

<style scoped>
.express-admin-page {
  min-height: 100vh;
  background: linear-gradient(180deg, #edf4ff 0%, #f7faff 100%);
  padding: 24px 16px 40px;
  box-sizing: border-box;
}

.page-shell {
  max-width: 1200px;
  margin: 0 auto;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  margin-bottom: 18px;
}

.page-header h1 {
  margin: 0 0 8px;
  font-size: 28px;
  color: #183153;
}

.page-header p {
  margin: 0;
  color: #607187;
}

.header-actions,
.filter-buttons,
.record-actions,
.pagination-bar {
  display: flex;
  gap: 12px;
  align-items: center;
}

.header-actions {
  flex-wrap: wrap;
  justify-content: flex-end;
}

.filter-buttons {
  flex-wrap: wrap;
  justify-content: flex-start;
}

.record-actions {
  flex-wrap: wrap;
  justify-content: flex-start;
}

.pagination-bar {
  justify-content: center;
}

.filter-card,
.summary-card,
.record-card {
  background: #fff;
  border-radius: 18px;
  box-shadow: 0 12px 30px rgba(33, 94, 216, 0.08);
}

.filter-card {
  padding: 16px;
  margin-bottom: 16px;
}

.filter-grid {
  display: grid;
  grid-template-columns: minmax(0, 2fr) minmax(0, 1fr) auto;
  gap: 12px;
  align-items: end;
}

.filter-item label,
.record-label,
.summary-label {
  display: block;
  margin-bottom: 8px;
  color: #58708d;
  font-size: 13px;
}

.filter-item input,
.filter-item select {
  width: 100%;
  box-sizing: border-box;
  border: 1px solid #d7e3fb;
  border-radius: 12px;
  padding: 11px 12px;
  font-size: 14px;
  outline: none;
}

.bottom-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 20px;
  margin-top: 24px;
  padding: 16px 20px;
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 12px 30px rgba(33, 94, 216, 0.08);
}

.summary-mini {
  display: flex;
  gap: 20px;
  color: #58708d;
  font-size: 14px;
}

.summary-mini strong {
  color: #1f4fc9;
  font-size: 18px;
  font-weight: 700;
}

.record-list {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.record-card {
  display: grid;
  grid-template-columns: 240px minmax(0, 1fr);
  gap: 20px;
  padding: 20px;
}

.record-image {
  background: #f7fbff;
  border-radius: 12px;
  overflow: hidden;
  min-height: 160px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.record-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.record-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  margin-bottom: 16px;
}

.record-value {
  color: #1b2f49;
  font-size: 14px;
}

.record-value.tracking {
  font-size: 20px;
  font-weight: 700;
  color: #17345f;
}

.record-value.multiline {
  word-break: break-all;
  line-height: 1.5;
}

.record-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px 18px;
}

.info-block {
  min-width: 0;
}

.status-tag {
  flex-shrink: 0;
  border-radius: 999px;
  padding: 7px 12px;
  font-size: 13px;
  font-weight: 600;
}

.status-tag.checked {
  color: #177245;
  background: #ecfff3;
}

.status-tag.unchecked {
  color: #bd4a31;
  background: #fff1ec;
}

.tracking-number-section {
  flex: 1;
  min-width: 0;
}

.tracking-number-display {
  display: flex;
  align-items: center;
  gap: 8px;
}

.edit-tracking-btn {
  padding: 4px 12px;
  font-size: 12px;
  border: 1px solid #d7e3fb;
  border-radius: 6px;
  background: #f7fbff;
  color: #215ed8;
  cursor: pointer;
  transition: all 0.2s ease;
}

.edit-tracking-btn:hover {
  background: #eef4ff;
}

.tracking-number-edit {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.edit-tracking-input {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #d7e3fb;
  border-radius: 8px;
  font-size: 14px;
  outline: none;
}

.edit-tracking-input:focus {
  border-color: #4b84ff;
  box-shadow: 0 0 0 3px rgba(75, 132, 255, 0.12);
}

.tracking-edit-actions {
  display: flex;
  gap: 8px;
}

.save-tracking-btn,
.cancel-tracking-btn {
  padding: 6px 16px;
  font-size: 12px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
}

.save-tracking-btn {
  background: linear-gradient(135deg, #215ed8, #4b84ff);
  color: white;
}

.cancel-tracking-btn {
  background: #f5f5f5;
  color: #666;
}

.status-and-delete {
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: flex-end;
}

.delete-btn {
  padding: 4px 10px;
  font-size: 12px;
  font-weight: 500;
  border: 1px solid #ffd7d7;
  border-radius: 6px;
  background: #fff5f5;
  color: #d93025;
  cursor: pointer;
  transition: all 0.2s ease;
}

.delete-btn:hover {
  background: #ffe6e6;
}

.delete-btn:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

.record-actions {
  margin-top: 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-items: center;
}

.primary-btn,
.light-btn {
  border: none;
  border-radius: 10px;
  padding: 10px 20px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  text-decoration: none;
  white-space: nowrap;
  transition: all 0.2s ease;
}

.primary-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(33, 94, 216, 0.3);
}

.light-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(33, 94, 216, 0.15);
}

.primary-btn {
  background: linear-gradient(135deg, #215ed8, #4b84ff);
  color: #fff;
}

.primary-btn-large {
  width: 100%;
  max-width: 280px;
  padding: 14px 24px;
  font-size: 16px;
  border-radius: 12px;
}

.light-btn {
  background: #eef4ff;
  color: #215ed8;
}

.light-btn-inline {
  color: #6b7a90;
  font-size: 13px;
  text-decoration: underline;
  cursor: pointer;
  transition: color 0.2s ease;
}

.light-btn-inline:hover {
  color: #215ed8;
}

.primary-btn:disabled,
.light-btn:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

.inline-link {
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.empty-block {
  padding: 48px 20px;
  background: #fff;
  border-radius: 18px;
  text-align: center;
  color: #5c718d;
  box-shadow: 0 12px 30px rgba(33, 94, 216, 0.08);
}

.pagination-bar {
  justify-content: center;
  margin-top: 18px;
}

.page-text {
  color: #536a87;
  font-size: 14px;
}

@media (max-width: 900px) {
  .page-header,
  .filter-grid,
  .record-card {
    grid-template-columns: 1fr;
  }

  .page-header {
    display: block;
  }

  .header-actions {
    margin-top: 14px;
    justify-content: flex-start;
  }

  .record-grid {
    grid-template-columns: 1fr;
  }

  .bottom-bar {
    flex-direction: column;
    gap: 16px;
  }
}

@media (max-width: 640px) {
  .express-admin-page {
    padding: 16px 12px 28px;
  }

  .record-card {
    padding: 16px;
  }

  .record-image {
    min-height: 200px;
  }

  .primary-btn,
  .light-btn {
    padding: 10px 16px;
    font-size: 14px;
  }

  .bottom-bar {
    padding: 14px;
  }
}

/* 图片预览弹窗样式 */
.image-preview-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.85);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 20px;
}

.image-preview-container {
  background: #fff;
  border-radius: 16px;
  max-width: 100%;
  max-height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.preview-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #e8f0fd;
  background: #f7fbff;
}

.preview-title {
  font-size: 16px;
  font-weight: 600;
  color: #183153;
}

.preview-close-btn {
  width: 36px;
  height: 36px;
  border: none;
  background: #eef4ff;
  border-radius: 50%;
  font-size: 24px;
  color: #607187;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.preview-close-btn:hover {
  background: #215ed8;
  color: #fff;
}

.preview-image-wrapper {
  padding: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  overflow: hidden;
}

.preview-image-wrapper img {
  max-width: 100%;
  max-height: 80vh;
  object-fit: contain;
  border-radius: 8px;
}
</style>
